import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { loadModelGLTF } from "../utils/loadingManager";
import { createGradientCircle } from "../utils/threeJsUtils";
import { isApartmentAvailable } from "../utils/apartmentStatus";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const BasicScene = ({
  elementId,
  touredApartment,
  newProjectLoaded,
  tourMode = false,
  colors,
  apartments = [],
  projectDefaultView,
  models,
}) => {
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const meshRef = useRef();
  const modelsRef = useRef([]);
  const materialRef = useRef([]);
  const humanGroupRef = useRef(new THREE.Group());
  const apartmentNodes = useRef([]);
  const vacantColor = useRef();
  const occupiedColor = useRef();
  const vacantHoveredColor = useRef();
  const occupiedHoveredColor = useRef();
  const controls = useRef();
  const gradientColors = [
    {
      position: 0,
      color: "black",
    },
    {
      position: 0.7,
      color: "black",
    },
    {
      position: 1,
      color: colors ? colors.primary : 0xff0000,
    },
  ];
  useEffect(() => {
    // Initialize the scene
    vacantColor.current = new THREE.MeshBasicMaterial({
      color: colors ? colors.primary : 0xff0000,
      transparent: true,
      opacity:
        projectDefaultView && projectDefaultView.transparency
          ? projectDefaultView.transparency
          : 0.3,
    });
    occupiedColor.current = new THREE.MeshBasicMaterial({
      color: 0xff8870,
      transparent: true,
      opacity:
        projectDefaultView && projectDefaultView.transparency
          ? projectDefaultView.transparency
          : 0.3,
    });
    vacantHoveredColor.current = new THREE.MeshBasicMaterial({
      color: colors ? colors.primary : 0xff0000,
      transparent: true,
      opacity: 0.9,
    });
    occupiedHoveredColor.current = new THREE.MeshBasicMaterial({
      color: "FF8870",
      transparent: true,
      opacity: 0.9,
    });
    sceneRef.current = new THREE.Scene();
    materialRef.current = new THREE.MeshBasicMaterial({
      color: colors ? colors.primary : 0xff0000,
      transparent: true,
      opacity: 0.9,
    });
    // Initialize the camera
    cameraRef.current = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    
    // Initialize the renderer
    rendererRef.current = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    const canvasContainer = document.getElementById(elementId);
    rendererRef.current.setSize(
      canvasContainer.clientWidth,
      canvasContainer.clientHeight
    );

    // Append the renderer to the DOM
    canvasContainer.appendChild(rendererRef.current.domElement);
    controls.current = new OrbitControls(
      cameraRef.current,
      rendererRef.current.domElement
    );
    controls.current.target.set(0, 3, 0);
    cameraRef.current.position.set(0, 3, 6);

    controls.current.screenSpacePanning = true;
    controls.current.enabled = true;
    controls.current.enableRotate = true;
    controls.current.enableDamping = true;
    controls.current.enableZoom = true;
    controls.current.enablePan = true;
    controls.current.dampingFactor = 0.035;
    controls.current.minDistance = 1;
    controls.current.maxDistance = 200;
    // Add lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    sceneRef.current.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5).normalize();
    sceneRef.current.add(directionalLight);

    const animate = () => {
      requestAnimationFrame(animate);
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      const width = canvasContainer.clientWidth;
      const height = canvasContainer.clientHeight;
      rendererRef.current.setSize(width, height);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    };

    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
      canvasContainer.removeChild(rendererRef.current.domElement);
    };
  }, [elementId]);

  useEffect(() => {
    // if (!modelsRef.current) {
      // console.log("building models", models);
      Object.keys(models).forEach((modelId) => {
        const model = models[modelId];
        // console.log("model", model);

        model.castShadow = true;
        model.receiveShadow = true;
        model.scale.set(0.05, 0.05, 0.05);
        model.traverse(function (node) {
          // if (node.isMesh) {
          //   node.castShadow = true;
          //   node.receiveShadow = true;
          // }

          if (node.name.includes("bbox")) {
            const app = apartments.find((app) =>
              app.title
                ? app.title == node.name.split("_")[0] ||
                  app.title == node.name.split("_")[1]
                : app.name == node.name.split("_")[0] ||
                  app.name == node.name.split("_")[1]
            );
            // console.log("app", app);
            if (app) {
              node.userData = {
                apartment: app,
              };

              apartmentNodes.current.push(node);
              if (
                isApartmentAvailable(node && node.userData.apartment)
              ) {
                node.material = vacantColor.current;
              } else {
                node.material = occupiedColor.current;
              }
            }
          }
        });
        // console.log("adding model", model);
        modelsRef.current.push(model);
        sceneRef.current.add(model);
        const circleRadius = 2.5;
        const circle = createGradientCircle(
            circleRadius,
            0,
            0,
            0,
            gradientColors,
            true
          );
        sceneRef.current.add(circle);
      });
    // }
  }, [models]);

  useEffect(() => {
    if (touredApartment) {
      if (!tourMode) {
        setAllApartmentsNodes([touredApartment.id]);
      }
      modelsRef.current.forEach((model) => {
        model.traverse(function (node) {
          // createGeometryFromNode(node);
          if (
            node.name.includes("bbox") &&
            node.name.includes(touredApartment.id)
          ) {
            node.material = materialRef.current;
          }
        });
      });
    } else {
      //   console.log("removing mesh", meshRef.current);

      if (meshRef.current) {
        sceneRef.current.remove(meshRef.current);

        meshRef.current = null;
      }
      if (humanGroupRef.current) humanGroupRef.current.visible = false;
      setAllApartmentsNodes();
    }

    return () => {};
  }, [touredApartment]);

  const setAllApartmentsNodes = (ids = []) => {
    // if (appRef.current) ids.push(appRef.current.id);

    if (apartmentNodes.current) {
      if (ids.length) {
        apartmentNodes.current.forEach((node) => {
          // console.log(node)
          if (node && node.userData.apartment) {
            // console.log("found node with app", node.userData.apartment)
            if (!ids.includes(node.userData.apartment.id)) {
              if (
                node && isApartmentAvailable(node.userData.apartment)
              ) {
                node.material = vacantColor.current;
              } else {
                node.material = occupiedColor.current;
              }
            } else {
              if (
                node && isApartmentAvailable(node.userData.apartment)
              ) {
                node.material = vacantHoveredColor.current;
              } else {
                node.material = occupiedHoveredColor.current;
              }
            }
          }
        });
      } else {
        apartmentNodes.current.forEach((node) => {
          // console.log(node)
          if (node && node.userData.apartment) {
            // console.log(node.userData.apartment.available)
            if (
              isApartmentAvailable(node && node.userData.apartment)
            ) {
              node.material = vacantColor.current;
            } else {
              node.material = occupiedColor.current;
            }
          }
        });
      }
    }
  };
  return null;
};

export default BasicScene;
